// craft-controls.ts — Work/Stop actions for the crafting Phase-3 WIP island (craft_controls).
//
// The island is a SERVER-rendered fragment (object_card_island_craft_controls_inc.php), so its
// buttons call this global by name (the lock island's useKeysOnLock pattern). The endpoints
// validate flag, permission, out-of-combat, tier, tools,
// dedupe) — the button conditions in the partial are UX, not authority. Success repaint rides
// the mutation's WS push: the card re-renders and the island re-fetches with fresh state, so
// the inline span is only immediate feedback.

import { actorCanReachTarget } from '../shared/proximity-browser';

const CRAFT_WIP_ACTIONS = new Set(['work', 'stop']);

async function ftCraftWipAction(
    action: string,
    targetId: string,
    displayId: string,
    workerCreatureId: string = '',
): Promise<void> {
    if (!CRAFT_WIP_ACTIONS.has(action) || !targetId) return;
    const btn = document.getElementById(`btn_craft_wip_${action}_${displayId}`) as HTMLButtonElement | null;
    const out = document.getElementById(`craft_wip_result_${displayId}`);
    const setOut = (html: string, cls: string): void => {
        if (!out) return;
        out.className = 'small ' + cls;
        out.innerHTML = html;
    };
    if (btn) btn.disabled = true;
    setOut('<span class="spinner-border spinner-border-sm me-1" role="status"></span>&hellip;', 'text-muted');
    try {
        let sc: Record<string, unknown> | null = null;
        try {
            const getSidelogContext = (window as Window & { getSidelogContext?: () => unknown }).getSidelogContext;
            if (typeof getSidelogContext === 'function') sc = getSidelogContext() as Record<string, unknown> | null;
        } catch (e) { sc = null; }
        if (action === 'work') {
            const within = await actorCanReachTarget(workerCreatureId, targetId);
            if (within !== true) {
                setOut(within === false ? 'You must be within reach of the unfinished item to work on it.' : 'Could not determine reach. Refresh the encounter and try again.', 'text-warning');
                if (btn) btn.disabled = false;
                return;
            }
        }
        // Client truth only (websocket/status-truth.ts ftCommitCraftingWip): the shared actor-row loader gets the worker's row (held, its
        // encounter loaded, or the encounter-less row itself); the server's answer (a refusal after the claim, a completion notice) is shown
        // here. The legacy work / stop_crafting_wip_inc.php endpoints are gone (N6 2026-10-01, R-083).
        const commit = window.ftCommitCraftingWip;
        if (typeof commit !== 'function') { setOut('Crafting is not available on this page &mdash; reload and try again.', 'text-warning'); if (btn) btn.disabled = false; return; }
        const a = await commit({ action: action === 'work' ? 'work' : 'stop', workerId: workerCreatureId,
            ...(action === 'work' ? { inventoryId: targetId } : { statusId: targetId }), encounterId: String(sc?.encounter_id ?? '') || undefined });
        if (a.ok && !a.refused) {
            if (a.notice) { if (out) { out.className = 'small text-warning'; out.textContent = a.notice; } }
            else setOut(action === 'work' ? 'You set to work &mdash; updating&hellip;' : 'You stop working &mdash; updating&hellip;', 'text-success');
        } else if (out) {
            out.className = 'small text-warning';
            out.textContent = a.refused ?? (a.status === 403 ? 'You do not have permission to this creature.' : a.error === 'stale' ? 'That changed elsewhere — try again.' : (a.error || 'The action was refused.'));
            if (btn) btn.disabled = false;
        }
    } catch (e) {
        setOut('Request failed &mdash; try again.', 'text-danger');
        if (btn) btn.disabled = false;
    }
}

declare global {
    interface Window {
        ftCraftWipAction: typeof ftCraftWipAction;
        ftCommitCraftingWip?: (input: { action: 'work' | 'stop'; workerId: string; inventoryId?: string; statusId?: string; encounterId?: string })
            => Promise<{ ok: boolean; status?: number; error?: string; refused?: string; notice?: string }>;
    }
}
window.ftCraftWipAction = ftCraftWipAction;

export {};
